import type { MeView, Role } from "../api/types";
import type { Access } from "./screens";

/** The roles the person holds in the discipline they work in now. */
export function activeRoles(me: MeView): readonly Role[] {
  return me.active?.roles ?? [];
}

/**
 * Whether a person may open a screen. This only decides what the browser shows; the server checks
 * the role of every request again.
 */
export function allows(me: MeView, access: Access): boolean {
  switch (access) {
    case "public":
    case "person":
      return true;
    case "admin":
      return me.is_admin;
    case "workspace":
      return !!me.active;
    case "engineer":
      return activeRoles(me).includes("engineer");
    case "sme":
      return activeRoles(me).includes("sme");
  }
}

/** Why a screen is not open to this person, in plain words. */
export function deniedMessage(me: MeView, access: Access): string {
  if (access === "admin") return "This screen is for the admin.";
  if (!me.active) {
    if (me.memberships.length > 0) return "Choose the discipline you want to work in, at the top of the page.";
    return me.is_admin
      ? "The admin account works on accounts and disciplines only. Use the Admin page."
      : "You are not a member of any discipline yet. Please ask the admin to add you.";
  }
  if (access === "engineer") return "This screen is for engineers. Your role in this discipline does not include it.";
  if (access === "sme") return "This screen is for subject matter experts (SMEs). Your role in this discipline does not include it.";
  return "This screen is not open to you.";
}

/** Where a person starts: the admin, who has no discipline, starts on the Admin page. */
export function homePath(me: MeView): string {
  return me.is_admin && !me.active ? "/admin" : "/";
}

/** The person's roles in words, for the top bar. */
export function rolesInWords(me: MeView): string {
  const words: string[] = [];
  if (me.is_admin) words.push("Admin");
  const roles = activeRoles(me);
  if (roles.includes("engineer") && roles.includes("sme")) words.push("Engineer and SME");
  else if (roles.includes("engineer")) words.push("Engineer");
  else if (roles.includes("sme")) words.push("SME");
  return words.join(", ");
}
